export interface GlossaryEntry {
  term: string;
  definition: string;
  category: string;
  relatedTerms: string[];
  references?: string[];
}

export const GLOSSARY_CATEGORIES = [
  'Theological Concepts',
  'Biblical Books and Divisions',
  'Religious Practices and Objects',
  'Biblical Terms',
  'Spiritual Beings',
  'Biblical Events',
  'Religious Groups',
  'Miscellaneous Terms'
] as const;

export const GLOSSARY_DATA: GlossaryEntry[] = [
  // Theological Concepts
  {
    term: 'Atonement',
    definition: 'The reconciliation of God and humankind through the sacrificial death of Jesus Christ.',
    category: 'Theological Concepts',
    relatedTerms: ['Sacrifice', 'Redemption', 'Salvation'],
    references: ['Romans 5:11', 'Hebrews 2:17']
  },
  {
    term: 'Baptism',
    definition: 'A Christian rite of admission and adoption, almost invariably with the use of water.',
    category: 'Theological Concepts',
    relatedTerms: ['Faith', 'Repentance', 'Salvation'],
    references: ['Matthew 3:16', 'Acts 2:38']
  },
  {
    term: 'Covenant',
    definition: 'A formal agreement or promise made between God and humans.',
    category: 'Theological Concepts',
    relatedTerms: ['Promise', 'Faith', 'Grace'],
    references: ['Genesis 9:11', 'Hebrews 8:6']
  },
  {
    term: 'Eschatology',
    definition: 'The study of end times and final events in biblical prophecy.',
    category: 'Theological Concepts',
    relatedTerms: ['Second Coming', 'Judgment Day', 'Revelation'],
    references: ['1 Thessalonians 4:13-18', 'Matthew 24:3-44', 'Revelation 20:1-15']
  },
  {
    term: 'Faith',
    definition: 'Complete trust or confidence in God and His promises.',
    category: 'Theological Concepts',
    relatedTerms: ['Grace', 'Salvation', 'Justification'],
    references: ['Hebrews 11:1', 'Romans 10:17']
  },
  {
    term: 'Grace',
    definition: 'The free and unmerited favor of God.',
    category: 'Theological Concepts',
    relatedTerms: ['Faith', 'Salvation', 'Mercy'],
    references: ['Ephesians 2:8-9', '2 Corinthians 12:9']
  },
  {
    term: 'Incarnation',
    definition: 'The doctrine of God becoming human in the person of Jesus Christ.',
    category: 'Theological Concepts',
    relatedTerms: ['Jesus Christ', 'Virgin Birth', 'Deity'],
    references: ['John 1:14', 'Philippians 2:5-8', 'Colossians 2:9']
  },
  {
    term: 'Justification',
    definition: 'The act of God declaring sinners righteous through faith in Christ.',
    category: 'Theological Concepts',
    relatedTerms: ['Faith', 'Grace', 'Salvation'],
    references: ['Romans 5:1', 'Galatians 2:16']
  },
  {
    term: 'Omniscience',
    definition: 'The attribute of God having complete and perfect knowledge of all things.',
    category: 'Theological Concepts',
    relatedTerms: ['God', 'Attributes of God', 'Sovereignty'],
    references: ['Psalm 139:1-6', '1 John 3:20', 'Hebrews 4:13']
  },
  {
    term: 'Predestination',
    definition: 'The doctrine that God has foreordained all things, especially concerning human salvation.',
    category: 'Theological Concepts',
    relatedTerms: ['Election', 'Sovereignty', 'Free Will'],
    references: ['Ephesians 1:4-5', 'Romans 8:29-30', 'Acts 13:48']
  },
  {
    term: 'Propitiation',
    definition: 'The turning away of God\'s wrath by an offering or sacrifice.',
    category: 'Theological Concepts',
    relatedTerms: ['Atonement', 'Sacrifice', 'Reconciliation'],
    references: ['Romans 3:25', '1 John 2:2', 'Hebrews 2:17']
  },
  {
    term: 'Redemption',
    definition: 'The act of being saved from sin, error, or evil.',
    category: 'Theological Concepts',
    relatedTerms: ['Salvation', 'Atonement', 'Grace'],
    references: ['Ephesians 1:7', 'Colossians 1:14']
  },
  {
    term: 'Repentance',
    definition: 'The action of sincere regret or remorse for past sins or wrongdoings.',
    category: 'Theological Concepts',
    relatedTerms: ['Faith', 'Salvation', 'Forgiveness'],
    references: ['Acts 3:19', '2 Corinthians 7:10']
  },
  {
    term: 'Resurrection',
    definition: 'The rising of Christ from the dead; the rising of the dead at the Last Judgment.',
    category: 'Theological Concepts',
    relatedTerms: ['Crucifixion', 'Salvation', 'Faith'],
    references: ['1 Corinthians 15:3-8', 'Romans 6:5', 'John 11:25-26']
  },
  {
    term: 'Salvation',
    definition: 'Deliverance from sin and its consequences through faith in Christ.',
    category: 'Theological Concepts',
    relatedTerms: ['Faith', 'Grace', 'Redemption'],
    references: ['John 3:16', 'Acts 4:12']
  },
  {
    term: 'Sanctification',
    definition: 'The process of being made holy and set apart for God\'s purpose.',
    category: 'Theological Concepts',
    relatedTerms: ['Holiness', 'Grace', 'Righteousness'],
    references: ['1 Thessalonians 4:3', '2 Thessalonians 2:13']
  },
  {
    term: 'Sin',
    definition: 'Any thought or action that falls short of God\'s perfect standard.',
    category: 'Theological Concepts',
    relatedTerms: ['Repentance', 'Grace', 'Salvation'],
    references: ['Romans 3:23', '1 John 1:8', 'James 4:17']
  },
  {
    term: 'Theophany',
    definition: 'A visible manifestation of God to a human being.',
    category: 'Theological Concepts',
    relatedTerms: ['God', 'Vision', 'Revelation'],
    references: ['Exodus 3:2-6', 'Genesis 32:22-30', 'Isaiah 6:1-5']
  },

  // Biblical Books and Divisions
  {
    term: 'Apocrypha',
    definition: 'Religious texts that are not part of the standard Biblical canon.',
    category: 'Biblical Books and Divisions',
    relatedTerms: ['Canon', 'Scripture', 'Bible'],
    references: ['2 Timothy 3:16', 'Revelation 22:18-19']
  },
  {
    term: 'Epistles',
    definition: 'Letters written by apostles to early Christian churches or individuals.',
    category: 'Biblical Books and Divisions',
    relatedTerms: ['New Testament', 'Paul', 'Apostle']
  },
  {
    term: 'Gospels',
    definition: 'The first four books of the New Testament, telling the story of Jesus\'s life.',
    category: 'Biblical Books and Divisions',
    relatedTerms: ['New Testament', 'Christ', 'Apostle']
  },
  {
    term: 'New Testament',
    definition: 'The second part of the Christian Bible, focusing on Jesus Christ and the early church.',
    category: 'Biblical Books and Divisions',
    relatedTerms: ['Gospels', 'Epistles', 'Old Testament']
  },
  {
    term: 'Old Testament',
    definition: 'The first part of the Christian Bible, containing the sacred writings of the Jews.',
    category: 'Biblical Books and Divisions',
    relatedTerms: ['Pentateuch', 'Prophets', 'Psalms']
  },
  {
    term: 'Pentateuch',
    definition: 'The first five books of the Bible (Genesis through Deuteronomy), also called the Torah.',
    category: 'Biblical Books and Divisions',
    relatedTerms: ['Torah', 'Moses', 'Law'],
    references: ['Luke 24:44', 'John 1:45', 'Acts 28:23']
  },
  {
    term: 'Prophets',
    definition: 'Books of the Old Testament written by God\'s messengers predicting future events.',
    category: 'Biblical Books and Divisions',
    relatedTerms: ['Old Testament', 'Prophet', 'Prophecy']
  },
  {
    term: 'Psalms',
    definition: 'A book of sacred songs, prayers, and poems in the Old Testament.',
    category: 'Biblical Books and Divisions',
    relatedTerms: ['Old Testament', 'David', 'Worship']
  },
  {
    term: 'Septuagint',
    definition: 'The ancient Greek translation of the Hebrew Bible, often abbreviated as LXX.',
    category: 'Biblical Books and Divisions',
    relatedTerms: ['Old Testament', 'Greek', 'Translation'],
    references: ['Acts 8:32-33', 'Romans 3:13-18', 'Hebrews 1:6']
  },
  {
    term: 'Torah',
    definition: 'The first five books of the Hebrew Bible: Genesis, Exodus, Leviticus, Numbers, and Deuteronomy.',
    category: 'Biblical Books and Divisions',
    relatedTerms: ['Pentateuch', 'Law', 'Moses'],
    references: ['Joshua 1:8', 'Psalm 1:2']
  },

  // Religious Practices and Objects
  {
    term: 'Altar',
    definition: 'A raised structure used for religious offerings or worship.',
    category: 'Religious Practices and Objects',
    relatedTerms: ['Sacrifice', 'Worship', 'Temple'],
    references: ['Genesis 8:20', 'Exodus 27:1-8', 'Hebrews 13:10']
  },
  {
    term: 'Anointing',
    definition: 'The ritual application of oil to persons or objects for consecration.',
    category: 'Religious Practices and Objects',
    relatedTerms: ['Oil', 'Consecration', 'Priesthood'],
    references: ['Exodus 30:22-33', 'James 5:14', '1 Samuel 16:13']
  },
  {
    term: 'Circumcision',
    definition: 'A religious rite marking the covenant between God and Abraham\'s descendants.',
    category: 'Religious Practices and Objects',
    relatedTerms: ['Covenant', 'Abraham', 'Judaism']
  },
  {
    term: 'Communion',
    definition: 'A Christian sacrament commemorating the Last Supper.',
    category: 'Religious Practices and Objects',
    relatedTerms: ['Last Supper', 'Bread', 'Wine']
  },
  {
    term: 'Ephod',
    definition: 'A sacred garment worn by the Jewish high priests.',
    category: 'Religious Practices and Objects',
    relatedTerms: ['High Priest', 'Temple', 'Worship'],
    references: ['Exodus 28:6-14', '1 Samuel 23:9-12', 'Hosea 3:4']
  },
  {
    term: 'Fasting',
    definition: 'Abstaining from food for spiritual purposes.',
    category: 'Religious Practices and Objects',
    relatedTerms: ['Prayer', 'Repentance', 'Worship']
  },
  {
    term: 'Laying on of Hands',
    definition: 'A religious practice of placing hands on someone for blessing, healing, or ordination.',
    category: 'Religious Practices and Objects',
    relatedTerms: ['Blessing', 'Ordination', 'Prayer'],
    references: ['Acts 6:6', '1 Timothy 4:14', 'Hebrews 6:2']
  },
  {
    term: 'Menorah',
    definition: 'A seven-branched lampstand used in the Temple, symbolizing God\'s light.',
    category: 'Religious Practices and Objects',
    relatedTerms: ['Temple', 'Worship', 'Light'],
    references: ['Exodus 25:31-40', 'Zechariah 4:2']
  },
  {
    term: 'Phylacteries',
    definition: 'Small leather boxes containing Scripture verses worn by Jewish men during prayer.',
    category: 'Religious Practices and Objects',
    relatedTerms: ['Torah', 'Prayer', 'Jewish Customs', 'Law'],
    references: ['Matthew 23:5', 'Deuteronomy 6:8', 'Exodus 13:9']
  },
  {
    term: 'Sacrifice',
    definition: 'An offering to God, often involving the ritual slaughter of animals.',
    category: 'Religious Practices and Objects',
    relatedTerms: ['Altar', 'Atonement', 'Temple']
  },
  {
    term: 'Shema',
    definition: 'The central prayer in Judaism, beginning with "Hear, O Israel."',
    category: 'Religious Practices and Objects',
    relatedTerms: ['Prayer', 'Judaism', 'Commandment'],
    references: ['Deuteronomy 6:4-9', 'Mark 12:29-30', 'Luke 10:27']
  },
  {
    term: 'Tabernacle',
    definition: 'The portable sanctuary carried by the Israelites in the wilderness.',
    category: 'Religious Practices and Objects',
    relatedTerms: ['Temple', 'Worship', 'Moses'],
    references: ['Exodus 25-27', 'Hebrews 9:1-10', 'Acts 7:44']
  },
  {
    term: 'Temple',
    definition: 'The central place of worship in Jerusalem.',
    category: 'Religious Practices and Objects',
    relatedTerms: ['Tabernacle', 'Sacrifice', 'Worship'],
    references: ['1 Kings 6:1-38', '2 Chronicles 3:1-17', 'John 2:19-21']
  },
  {
    term: 'Tithe',
    definition: 'A tenth part of something paid as a contribution to religious organization.',
    category: 'Religious Practices and Objects',
    relatedTerms: ['Offering', 'Worship', 'Stewardship']
  },
  {
    term: 'Urim and Thummim',
    definition: 'Sacred lots used by the high priest to determine God\'s will in the Old Testament.',
    category: 'Religious Practices and Objects',
    relatedTerms: ['High Priest', 'Divination', 'Priesthood'],
    references: ['Exodus 28:30', '1 Samuel 28:6', 'Numbers 27:21']
  },

  // Biblical Terms
  {
    term: 'Abomination of Desolation',
    definition: 'A prophetic term referring to the desecration of the Temple and a sign of the end times.',
    category: 'Biblical Terms',
    relatedTerms: ['Prophecy', 'End Times', 'Temple'],
    references: ['Daniel 9:27', 'Matthew 24:15', 'Mark 13:14']
  },
  {
    term: 'Amen',
    definition: 'A solemn expression of agreement, meaning "so be it."',
    category: 'Biblical Terms',
    relatedTerms: ['Prayer', 'Worship', 'Faith'],
    references: ['Revelation 3:14', '2 Corinthians 1:20']
  },
  {
    term: 'Apocalypse',
    definition: 'A revelation or prophecy of the end times.',
    category: 'Biblical Terms',
    relatedTerms: ['Revelation', 'Prophecy', 'End Times'],
    references: ['Revelation 1:1', 'Daniel 12:1-4', '2 Peter 3:10']
  },
  {
    term: 'Apostle',
    definition: 'One of the twelve disciples chosen by Jesus to preach the gospel.',
    category: 'Biblical Terms',
    relatedTerms: ['Disciple', 'Jesus', 'Church'],
    references: ['Matthew 10:2-4', 'Acts 1:21-26', 'Ephesians 4:11']
  },
  {
    term: 'Beatitudes',
    definition: 'The blessings pronounced by Jesus at the beginning of the Sermon on the Mount.',
    category: 'Biblical Terms',
    relatedTerms: ['Sermon on the Mount', 'Jesus Christ', 'Teaching'],
    references: ['Matthew 5:3-12', 'Luke 6:20-23']
  },
  {
    term: 'Christ',
    definition: 'Title meaning "Anointed One," equivalent to the Hebrew "Messiah."',
    category: 'Biblical Terms',
    relatedTerms: ['Jesus', 'Messiah', 'Savior'],
    references: ['Matthew 16:16', 'John 20:31', 'Acts 2:36']
  },
  {
    term: 'Firstfruits',
    definition: 'The first portion of a harvest offered to God; also refers to Christ\'s resurrection.',
    category: 'Biblical Terms',
    relatedTerms: ['Offering', 'Harvest', 'Resurrection'],
    references: ['Exodus 23:19', '1 Corinthians 15:20-23', 'James 1:18']
  },
  {
    term: 'Gehenna',
    definition: 'A place or state of punishment and destruction, often associated with final judgment.',
    category: 'Biblical Terms',
    relatedTerms: ['Hell', 'Judgment', 'Punishment'],
    references: ['Matthew 5:22', 'Mark 9:43-48', 'James 3:6']
  },
  {
    term: 'Gospel',
    definition: 'The good news of salvation through Jesus Christ.',
    category: 'Biblical Terms',
    relatedTerms: ['Salvation', 'Jesus Christ', 'Faith'],
    references: ['Romans 1:16', 'Mark 1:1', '1 Corinthians 15:1-4']
  },
  {
    term: 'Hallelujah',
    definition: 'A Hebrew expression meaning "Praise the Lord!" used in worship.',
    category: 'Biblical Terms',
    relatedTerms: ['Praise', 'Worship', 'Psalms'],
    references: ['Revelation 19:1', 'Psalm 150:6', 'Psalm 146:1', 'Revelation 19:1-6']
  },
  {
    term: 'Leviathan',
    definition: 'A powerful sea creature mentioned in the Old Testament, often symbolizing chaos or evil.',
    category: 'Biblical Terms',
    relatedTerms: ['Creation', 'Sea', 'Creature'],
    references: ['Job 41:1-34', 'Psalm 74:14', 'Isaiah 27:1']
  },
  {
    term: 'Maranatha',
    definition: 'An Aramaic expression meaning "Our Lord, come!" used in early Christian prayers and worship.',
    category: 'Biblical Terms',
    relatedTerms: ['Prayer', 'Second Coming', 'Jesus Christ', 'Worship'],
    references: ['1 Corinthians 16:22', 'Revelation 22:20', 'Philippians 4:5']
  },
  {
    term: 'Manna',
    definition: 'The miraculous food God provided for the Israelites in the wilderness.',
    category: 'Biblical Terms',
    relatedTerms: ['Exodus', 'Wilderness', 'Bread'],
    references: ['Exodus 16:31-35', 'John 6:31-35', 'Revelation 2:17']
  },
  {
    term: 'Melchizedek',
    definition: 'The king of Salem and priest of God Most High who blessed Abraham.',
    category: 'Biblical Terms',
    relatedTerms: ['Priesthood', 'Abraham', 'Jesus Christ'],
    references: ['Genesis 14:18-20', 'Psalm 110:4', 'Hebrews 7:1-17']
  },
  {
    term: 'Messiah',
    definition: 'The promised deliverer of the Jewish nation prophesied in the Hebrew Bible.',
    category: 'Biblical Terms',
    relatedTerms: ['Jesus Christ', 'Prophecy', 'Savior'],
    references: ['Daniel 9:25-26', 'Isaiah 53:1-12', 'John 4:25-26']
  },
  {
    term: 'Parable',
    definition: 'A simple story used to illustrate a moral or spiritual lesson.',
    category: 'Biblical Terms',
    relatedTerms: ['Teaching', 'Jesus Christ', 'Kingdom'],
    references: ['Matthew 13:34-35', 'Mark 4:33-34', 'Luke 15:3']
  },
  {
    term: 'Prophet',
    definition: 'A person regarded as an inspired teacher or proclaimer of God\'s will.',
    category: 'Biblical Terms',
    relatedTerms: ['Prophecy', 'Revelation', 'Message'],
    references: ['Deuteronomy 18:15', 'Amos 3:7', 'Acts 3:22']
  },
  {
    term: 'Selah',
    definition: 'A Hebrew word found in the Psalms, likely indicating a musical pause or emphasis.',
    category: 'Biblical Terms',
    relatedTerms: ['Psalms', 'Worship', 'Music'],
    references: ['Psalm 3:2', 'Psalm 46:7']
  },

  // Spiritual Beings
  {
    term: 'Angel',
    definition: 'A spiritual being acting as a divine messenger.',
    category: 'Spiritual Beings',
    relatedTerms: ['Heaven', 'Messenger', 'Spirit'],
    references: ['Hebrews 1:14', 'Psalm 91:11', 'Luke 1:26-28']
  },
  {
    term: 'Archangel',
    definition: 'A chief or ruling angel, with Michael being explicitly named as one.',
    category: 'Spiritual Beings',
    relatedTerms: ['Angels', 'Michael', 'Gabriel'],
    references: ['1 Thessalonians 4:16', 'Jude 1:9', 'Revelation 12:7']
  },
  {
    term: 'Cherubim',
    definition: 'A high-ranking order of angels, often associated with God\'s presence and glory.',
    category: 'Spiritual Beings',
    relatedTerms: ['Angel', 'Seraphim', 'Heaven'],
    references: ['Ezekiel 10:1-22', 'Genesis 3:24', 'Exodus 25:18-22']
  },
  {
    term: 'Demon',
    definition: 'An evil spirit serving Satan.',
    category: 'Spiritual Beings',
    relatedTerms: ['Demon', 'Evil', 'Devil'],
    references: ['Mark 5:1-20', 'Matthew 12:43-45', 'James 2:19']
  },
  {
    term: 'Living Creatures',
    definition: 'Heavenly beings seen in visions by Ezekiel and John, surrounding God\'s throne.',
    category: 'Spiritual Beings',
    relatedTerms: ['Angels', 'Throne', 'Vision'],
    references: ['Ezekiel 1:5-14', 'Revelation 4:6-9', 'Revelation 5:6-14']
  },
  {
    term: 'Principalities',
    definition: 'A category of spiritual powers mentioned in Scripture, often referring to angelic or demonic authorities.',
    category: 'Spiritual Beings',
    relatedTerms: ['Powers', 'Angels', 'Spiritual Warfare'],
    references: ['Ephesians 6:12', 'Colossians 1:16']
  },
  {
    term: 'Satan',
    definition: 'The chief evil spirit; the great adversary of God and humanity.',
    category: 'Spiritual Beings',
    relatedTerms: ['Devil', 'Evil', 'Temptation'],
    references: ['Job 1:6-12', 'Matthew 4:1-11', 'Revelation 12:9']
  },
  {
    term: 'Seraphim',
    definition: 'A high-ranking order of angels described as having six wings, serving before God\'s throne.',
    category: 'Spiritual Beings',
    relatedTerms: ['Angels', 'Cherubim', 'Heaven'],
    references: ['Isaiah 6:2-3']
  },

  // Biblical Events
  {
    term: 'Ascension',
    definition: 'The bodily rising of Jesus Christ into heaven after His resurrection.',
    category: 'Biblical Events',
    relatedTerms: ['Resurrection', 'Jesus Christ', 'Second Coming'],
    references: ['Acts 1:9-11', 'Luke 24:50-53', 'Mark 16:19']
  },
  {
    term: 'Babel',
    definition: 'The event where God confused human language and scattered people across the earth.',
    category: 'Biblical Events',
    relatedTerms: ['Languages', 'Nations', 'Pride'],
    references: ['Genesis 11:1-9', 'Acts 2:1-11', 'Zephaniah 3:9']
  },
  {
    term: 'Creation',
    definition: 'God\'s act of bringing the universe into existence.',
    category: 'Biblical Events',
    relatedTerms: ['Genesis', 'God', 'Universe'],
    references: ['Genesis 1:1-31', 'John 1:1-3', 'Colossians 1:16']
  },
  {
    term: 'Crucifixion',
    definition: 'The execution of Jesus Christ on a cross.',
    category: 'Biblical Events',
    relatedTerms: ['Jesus Christ', 'Atonement', 'Salvation'],
    references: ['Matthew 27:32-56', 'Mark 15:21-41', 'John 19:17-37']
  },
  {
    term: 'Day of Pentecost',
    definition: 'The day when the Holy Spirit descended upon the disciples, marking the birth of the Church.',
    category: 'Biblical Events',
    relatedTerms: ['Holy Spirit', 'Church', 'Tongues'],
    references: ['Acts 2:1-4']
  },
  {
    term: 'Exodus',
    definition: 'The departure of the Israelites from Egypt under Moses.',
    category: 'Biblical Events',
    relatedTerms: ['Moses', 'Israel', 'Egypt'],
    references: ['Exodus 12:31-42', 'Exodus 14:21-31', 'Psalm 105:37-38']
  },
  {
    term: 'Fall',
    definition: 'Adam and Eve\'s disobedience and expulsion from Eden.',
    category: 'Biblical Events',
    relatedTerms: ['Sin', 'Adam', 'Eve'],
    references: ['Genesis 3:1-24', 'Romans 5:12', '1 Timothy 2:14']
  },
  {
    term: 'Flood',
    definition: 'The great deluge in Noah\'s time that covered the earth.',
    category: 'Biblical Events',
    relatedTerms: ['Noah', 'Ark', 'Judgment'],
    references: ['Genesis 6:9-9:17', '2 Peter 2:5', 'Matthew 24:37-39']
  },
  {
    term: 'Judgment Day',
    definition: 'The final judgment of all humanity by God.',
    category: 'Biblical Events',
    relatedTerms: ['Second Coming', 'Resurrection', 'Judgment'],
    references: ['Matthew 25:31-46', 'Revelation 20:11-15', '2 Peter 3:7']
  },
  {
    term: 'Passover',
    definition: 'The Jewish festival commemorating the Exodus from Egypt.',
    category: 'Biblical Events',
    relatedTerms: ['Exodus', 'Moses', 'Feast'],
    references: ['Exodus 12:1-28', 'Luke 22:7-20', '1 Corinthians 5:7']
  },
  {
    term: 'Pentecost',
    definition: 'The descent of the Holy Spirit on the disciples after Jesus\'s ascension.',
    category: 'Biblical Events',
    relatedTerms: ['Holy Spirit', 'Church', 'Apostles'],
    references: ['Acts 2:1-4', 'Acts 2:38', 'Joel 2:28-29']
  },
  {
    term: 'Transfiguration',
    definition: 'The event where Jesus\' appearance was transformed and glorified before Peter, James, and John.',
    category: 'Biblical Events',
    relatedTerms: ['Jesus Christ', 'Moses', 'Elijah'],
    references: ['Matthew 17:1-9', 'Mark 9:2-8']
  },

  // Religious Groups
  {
    term: 'Essenes',
    definition: 'A Jewish sect known for their ascetic lifestyle and the Dead Sea Scrolls.',
    category: 'Religious Groups',
    relatedTerms: ['Dead Sea Scrolls', 'Judaism', 'Qumran'],
    references: []
  },
  {
    term: 'Gnosticism',
    definition: 'An early heretical movement emphasizing secret knowledge and rejecting the material world.',
    category: 'Religious Groups',
    relatedTerms: ['Heresy', 'Knowledge', 'Early Church'],
    references: ['1 Timothy 6:20-21', 'Colossians 2:8', '1 John 4:1-3']
  },
  {
    term: 'Hellenists',
    definition: 'Greek-speaking Jews who adopted elements of Greek culture.',
    category: 'Religious Groups',
    relatedTerms: ['Jews', 'Greeks', 'Culture'],
    references: ['Acts 6:1', 'Acts 9:29', 'Acts 11:20']
  },
  {
    term: 'Judaizers',
    definition: 'Early Christians who insisted Gentile converts must follow Jewish law.',
    category: 'Religious Groups',
    relatedTerms: ['Law', 'Gentiles', 'Circumcision'],
    references: ['Galatians 2:14', 'Acts 15:1-5', 'Philippians 3:2']
  },
  {
    term: 'Nicolaitans',
    definition: 'A heretical group mentioned in Revelation who promoted compromise with pagan practices.',
    category: 'Religious Groups',
    relatedTerms: ['Heresy', 'Early Church', 'False Teaching'],
    references: ['Revelation 2:6', 'Revelation 2:15']
  },
  {
    term: 'Pharisees',
    definition: 'A Jewish sect known for strict observance of religious ceremonies and practices.',
    category: 'Religious Groups',
    relatedTerms: ['Judaism', 'Law', 'Tradition'],
    references: ['Matthew 23:1-36', 'Luke 18:9-14', 'Acts 23:6']
  },
  {
    term: 'Sadducees',
    definition: 'A Jewish sect that denied the resurrection and the existence of angels.',
    category: 'Religious Groups',
    relatedTerms: ['Judaism', 'Temple', 'Priests'],
    references: ['Matthew 22:23-33', 'Acts 23:8', 'Acts 5:17']
  },
  {
    term: 'Samaritans',
    definition: 'A people of central Palestine, historically at odds with the Jews.',
    category: 'Religious Groups',
    relatedTerms: ['Jews', 'Gentiles', 'Temple'],
    references: ['John 4:9', 'Luke 10:33', 'Acts 8:5-8']
  },
  {
    term: 'Scribes',
    definition: 'Professional copyists and interpreters of the Jewish law.',
    category: 'Religious Groups',
    relatedTerms: ['Pharisees', 'Law', 'Torah'],
    references: ['Matthew 7:29', 'Mark 12:38-40', 'Luke 20:46']
  },
  {
    term: 'Zealots',
    definition: 'A Jewish political movement devoted to opposing Roman rule through violent means.',
    category: 'Religious Groups',
    relatedTerms: ['Jews', 'Romans', 'Politics'],
    references: ['Luke 6:15', 'Acts 1:13', 'Acts 21:20']
  },

  // Miscellaneous Terms
  {
    term: 'Gentile',
    definition: 'Any person not of Jewish faith or origin.',
    category: 'Miscellaneous Terms',
    relatedTerms: ['Nations', 'Judaism', 'Paul'],
    references: ['Acts 10:45', 'Romans 11:11', 'Ephesians 3:6']
  },
  {
    term: 'Hosanna',
    definition: 'Expression of praise meaning "Save us, we pray."',
    category: 'Miscellaneous Terms',
    relatedTerms: ['Praise', 'Worship', 'Salvation'],
    references: ['Matthew 21:9', 'Mark 11:9-10', 'John 12:13']
  },
  {
    term: 'Rabbi',
    definition: 'A Jewish teacher or religious leader.',
    category: 'Miscellaneous Terms',
    relatedTerms: ['Teacher', 'Judaism', 'Law'],
    references: ['Matthew 23:8', 'John 1:38', 'John 3:2']
  },
  {
    term: 'Sabbath',
    definition: 'The seventh day of the week, set aside for rest and worship.',
    category: 'Miscellaneous Terms',
    relatedTerms: ['Rest', 'Commandment', 'Worship'],
    references: ['Exodus 20:8-11', 'Mark 2:27-28', 'Hebrews 4:9-10']
  },
  {
    term: 'Shekinah',
    definition: 'The divine presence or manifestation of God dwelling among His people.',
    category: 'Miscellaneous Terms',
    relatedTerms: ['Glory', 'Presence', 'Temple'],
    references: ['Exodus 40:34-35', 'Numbers 9:15-16']
  },
  {
    term: 'Sheol',
    definition: 'The Hebrew underworld; the place of the dead.',
    category: 'Miscellaneous Terms',
    relatedTerms: ['Death', 'Grave', 'Hades'],
    references: ['Psalm 16:10', 'Genesis 37:35', 'Job 7:9']
  },
  {
    term: 'Synagogue',
    definition: 'A Jewish place of worship and religious instruction.',
    category: 'Miscellaneous Terms',
    relatedTerms: ['Temple', 'Worship', 'Judaism'],
    references: ['Luke 4:16', 'Acts 13:14-15', 'James 2:2']
  }
];
